import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import { ArrowLeft, Plus, FileText, Clock, AlertTriangle, CheckCircle2, Users, BarChart3, Bell, BellRing, RotateCcw, Trash2, CreditCard, Send, Loader2, ShieldCheck } from "lucide-react";

const cycleLabels = { monthly: "Monthly", quarterly: "Quarterly", custom: "Custom" };
const overageLabels = { block: "Block Work", allow_bill: "Allow + Bill Extra", alert_only: "Alert Only" };
const overageColors = {
  block: "bg-red-100 text-red-700",
  allow_bill: "bg-amber-100 text-amber-700",
  alert_only: "bg-blue-100 text-blue-700",
};

export default function SLAContractManagement() {
  const navigate = useNavigate();
  const [contracts, setContracts] = useState([]);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [summaries, setSummaries] = useState({});
  const [alerts, setAlerts] = useState([]);
  const [alertsByContract, setAlertsByContract] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAlerts, setShowAlerts] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({
    client_id: "",
    product_id: "",
    title: "",
    total_hours: "",
    monthly_fee: "",
    billing_cycle: "monthly",
    contract_start: new Date().toISOString().split("T")[0],
    contract_end: "",
    hourly_rate: "",
    overage_rate: "",
    response_time_standard: "",
    response_time_emergency: "",
    hour_rollover: false,
    overage_handling: "alert_only",
    notes: "",
    payment_method_type: "card",
    payment_processor: "fiserv",
  });
  const [editOpen, setEditOpen] = useState(false);
  const [editContract, setEditContract] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [autoChargeStatus, setAutoChargeStatus] = useState(null);
  const [sendingCardLink, setSendingCardLink] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [contractsRes, usersRes, productsRes, alertsRes] = await Promise.all([
        apiClient.get("/sla-contracts"),
        apiClient.get("/users?role=client"),
        apiClient.get("/products?active_only=true"),
        apiClient.get("/sla-alerts"),
      ]);
      setContracts(contractsRes.data);
      setClients(usersRes.data);
      setProducts(productsRes.data);
      setAlerts(alertsRes.data);

      // Index alerts by contract_id
      const byContract = {};
      for (const a of alertsRes.data) {
        if (!byContract[a.contract_id]) byContract[a.contract_id] = [];
        byContract[a.contract_id].push(a);
      }
      setAlertsByContract(byContract);

      // Fetch summaries for each contract
      const sums = {};
      for (const c of contractsRes.data) {
        try {
          const res = await apiClient.get(`/sla-contracts/${c.id}/summary`);
          sums[c.id] = res.data;
        } catch { /* skip */ }
      }
      setSummaries(sums);
    } catch (error) {
      toast.error("Failed to load SLA data");
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- apiClient is module-level stable, state setters are stable
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        total_hours: parseFloat(form.total_hours),
        monthly_fee: form.monthly_fee ? parseFloat(form.monthly_fee) : null,
        hourly_rate: form.hourly_rate ? parseFloat(form.hourly_rate) : null,
        overage_rate: form.overage_rate ? parseFloat(form.overage_rate) : null,
        response_time_standard: form.response_time_standard || null,
        response_time_emergency: form.response_time_emergency || null,
        product_id: form.product_id === "none" ? null : form.product_id || null,
        contract_end: form.contract_end || null,
      };
      await apiClient.post("/sla-contracts", payload);
      toast.success("SLA Contract created");
      setCreateOpen(false);
      setForm({ client_id: "", product_id: "", title: "", total_hours: "", monthly_fee: "", billing_cycle: "monthly", contract_start: new Date().toISOString().split("T")[0], contract_end: "", hourly_rate: "", overage_rate: "", response_time_standard: "", response_time_emergency: "", hour_rollover: false, overage_handling: "alert_only", notes: "", payment_method_type: "card", payment_processor: "fiserv" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create contract");
    }
  };

  const handleDelete = async (contract) => {
    if (!window.confirm(`Delete contract "${contract.title}" for ${contract.client_name}? This cannot be undone.`)) return;
    try {
      await apiClient.delete(`/sla-contracts/${contract.id}`);
      toast.success(`Contract "${contract.title}" deleted`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete contract");
    }
  };

  const openEdit = (contract) => {
    setEditContract(contract);
    setEditForm({
      title: contract.title || "",
      total_hours: contract.total_hours || "",
      monthly_fee: contract.monthly_fee || "",
      billing_cycle: contract.billing_cycle || "monthly",
      contract_end: contract.contract_end || "",
      hourly_rate: contract.hourly_rate || "",
      overage_rate: contract.overage_rate || "",
      response_time_standard: contract.response_time_standard || "",
      response_time_emergency: contract.response_time_emergency || "",
      overage_handling: contract.overage_handling || "alert_only",
      notes: contract.notes || "",
      is_active: contract.is_active !== false,
      payment_method_type: contract.payment_method_type || "card",
      payment_processor: contract.payment_processor || "fiserv",
      auto_charge: contract.auto_charge || false,
      payment_method_source: contract.payment_method_source || "manual",
    });
    setAutoChargeStatus(null);
    setEditOpen(true);
    // Fetch live auto-charge status (saved card, next charge date)
    apiClient.get(`/admin/contracts/${contract.id}/auto-charge-status`)
      .then(res => setAutoChargeStatus(res.data))
      .catch(() => setAutoChargeStatus({ error: true }));
  };

  const handleSendCardSetupLink = async () => {
    if (!editContract) return;
    setSendingCardLink(true);
    try {
      const res = await apiClient.post(`/admin/contracts/${editContract.id}/send-card-setup-link`, {});
      toast.success(`Secure card setup link sent to ${res.data.recipient_email}`);
      // Refresh status
      const s = await apiClient.get(`/admin/contracts/${editContract.id}/auto-charge-status`);
      setAutoChargeStatus(s.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send card setup link");
    } finally {
      setSendingCardLink(false);
    }
  };

  const handleAttachExistingCard = async (paymentIntentId = null) => {
    if (!editContract) return;
    let piId = paymentIntentId;
    if (piId === null) {
      // Auto-search mode
      if (!window.confirm("Attach the customer's existing Stripe PaymentMethod (default on customer OR most-recent card OR any card on a linked guest/duplicate customer) to this contract? No card re-entry required. You'll still need to toggle Auto-Charge to enable it.")) return;
    }
    setSendingCardLink(true);
    try {
      const params = piId ? `?payment_intent_id=${encodeURIComponent(piId)}` : "";
      const res = await apiClient.post(`/admin/contracts/${editContract.id}/attach-existing-card${params}`, {});
      toast.success(`Attached ${res.data.payment_method.brand?.toUpperCase()} ****${res.data.payment_method.last4} — ${res.data.reason}`);
      const s = await apiClient.get(`/admin/contracts/${editContract.id}/auto-charge-status`);
      setAutoChargeStatus(s.data);
    } catch (error) {
      const detail = error.response?.data?.detail || "Failed to attach card";
      toast.error(detail, { duration: 12000 });
    } finally {
      setSendingCardLink(false);
    }
  };

  const handleAttachByPI = async () => {
    const piId = window.prompt(
      "Paste a Stripe PaymentIntent ID (pi_...) from the Stripe Dashboard.\n\n" +
      "Use this when a recent successful charge is on a guest/duplicate customer — we'll retrieve the PaymentMethod " +
      "from that PaymentIntent and try to attach it to this client's canonical Stripe customer for off-session reuse.\n\n" +
      "If Stripe says the PM was one-time-use only, we'll tell you to send the Secure Setup Link instead."
    );
    if (!piId || !piId.trim()) return;
    await handleAttachExistingCard(piId.trim());
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...editForm,
        total_hours: editForm.total_hours ? parseFloat(editForm.total_hours) : undefined,
        monthly_fee: editForm.monthly_fee ? parseFloat(editForm.monthly_fee) : null,
        hourly_rate: editForm.hourly_rate ? parseFloat(editForm.hourly_rate) : null,
        overage_rate: editForm.overage_rate ? parseFloat(editForm.overage_rate) : null,
        response_time_standard: editForm.response_time_standard || null,
        response_time_emergency: editForm.response_time_emergency || null,
        contract_end: editForm.contract_end || null,
      };
      await apiClient.patch(`/sla-contracts/${editContract.id}`, payload);
      toast.success("Contract updated successfully");
      setEditOpen(false);
      setEditContract(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update contract");
    }
  };

  const handleResetAlerts = async (contractId) => {
    try {
      await apiClient.delete(`/sla-alerts/contract/${contractId}/reset`);
      toast.success("Alerts reset — will re-trigger if threshold still exceeded");
      fetchData();
    } catch (error) {
      toast.error("Failed to reset alerts");
    }
  };

  const handleDeleteAlert = async (alertId) => {
    try {
      await apiClient.delete(`/sla-alerts/${alertId}`);
      toast.success("Alert dismissed");
      fetchData();
    } catch (error) {
      toast.error("Failed to dismiss alert");
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="sla-management-page">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/admin")}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="font-heading text-2xl font-bold text-slate-900">SLA Contracts</h1>
            <p className="text-slate-500 mt-1">Manage client service level agreements and hours tracking</p>
          </div>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className="bg-sag-navy hover:bg-sag-navy-800" data-testid="create-sla-button">
                <Plus className="h-4 w-4 mr-2" /> New Contract
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">Create SLA Contract</DialogTitle>
                <DialogDescription>Set up a new service level agreement for a client.</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Client *</Label>
                  <Select value={form.client_id || "placeholder"} onValueChange={(v) => setForm({ ...form, client_id: v === "placeholder" ? "" : v })}>
                    <SelectTrigger data-testid="sla-client-select"><SelectValue placeholder="Select client..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="placeholder" disabled>Select client...</SelectItem>
                      {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.full_name} ({c.email})</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Product (Optional)</Label>
                  <Select value={form.product_id || "none"} onValueChange={(v) => setForm({ ...form, product_id: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">All Products</SelectItem>
                      {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Contract Title *</Label>
                  <Input placeholder="e.g., Monthly Support - 40 Hours" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required data-testid="sla-title-input" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Total Hours *</Label>
                    <Input type="number" step="0.5" min="1" placeholder="100" value={form.total_hours} onChange={(e) => setForm({ ...form, total_hours: e.target.value })} required data-testid="sla-hours-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Monthly Fee ($)</Label>
                    <Input type="number" step="0.01" min="0" placeholder="400.00" value={form.monthly_fee} onChange={(e) => setForm({ ...form, monthly_fee: e.target.value })} data-testid="sla-monthly-fee-input" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Hourly Rate ($)</Label>
                    <Input type="number" step="0.01" min="0" placeholder="150.00" value={form.hourly_rate} onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label>Overage Rate ($)</Label>
                    <Input type="number" step="0.01" min="0" placeholder="175.00" value={form.overage_rate} onChange={(e) => setForm({ ...form, overage_rate: e.target.value })} data-testid="sla-overage-rate-input" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Response Time (Standard)</Label>
                    <Input placeholder="e.g., 24 hours" value={form.response_time_standard} onChange={(e) => setForm({ ...form, response_time_standard: e.target.value })} data-testid="sla-response-std-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Response Time (Emergency)</Label>
                    <Input placeholder="e.g., 4 hours" value={form.response_time_emergency} onChange={(e) => setForm({ ...form, response_time_emergency: e.target.value })} data-testid="sla-response-emg-input" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Start Date *</Label>
                    <Input type="date" value={form.contract_start} onChange={(e) => setForm({ ...form, contract_start: e.target.value })} required />
                  </div>
                  <div className="space-y-2">
                    <Label>End Date</Label>
                    <Input type="date" value={form.contract_end} onChange={(e) => setForm({ ...form, contract_end: e.target.value })} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Billing Cycle</Label>
                    <Select value={form.billing_cycle} onValueChange={(v) => setForm({ ...form, billing_cycle: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="quarterly">Quarterly</SelectItem>
                        <SelectItem value="custom">Custom</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Overage Handling</Label>
                    <Select value={form.overage_handling} onValueChange={(v) => setForm({ ...form, overage_handling: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="alert_only">Alert Only</SelectItem>
                        <SelectItem value="allow_bill">Allow + Bill Extra</SelectItem>
                        <SelectItem value="block">Block Work</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" id="rollover" checked={form.hour_rollover} onChange={(e) => setForm({ ...form, hour_rollover: e.target.checked })} className="rounded border-slate-300" />
                  <Label htmlFor="rollover" className="cursor-pointer">Allow unused hours to roll over</Label>
                </div>
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea placeholder="Additional contract notes..." value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
                </div>
                {/* Payment Configuration */}
                <div className="border-t border-slate-200 pt-4 mt-2">
                  <p className="text-xs text-slate-500 uppercase tracking-wide font-medium mb-3">Payment Configuration</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Payment Method</Label>
                      <Select value={form.payment_method_type} onValueChange={(v) => setForm({ ...form, payment_method_type: v })}>
                        <SelectTrigger data-testid="sla-payment-method"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="card">Credit Card</SelectItem>
                          <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                          <SelectItem value="hybrid">Hybrid</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Processor</Label>
                      <Select value={form.payment_processor} onValueChange={(v) => setForm({ ...form, payment_processor: v })}>
                        <SelectTrigger data-testid="sla-payment-processor"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="fiserv">Scotiabank</SelectItem>
                          <SelectItem value="stripe">Stripe</SelectItem>
                          <SelectItem value="manual">Manual / Bank Transfer</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
                  <Button type="submit" className="bg-sag-navy hover:bg-sag-navy-800" disabled={!form.client_id || !form.title || !form.total_hours} data-testid="submit-sla-button">Create Contract</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card className="sag-card">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="p-3 rounded-xl bg-sag-navy/10 text-sag-navy"><FileText className="h-6 w-6" /></div>
              <div>
                <p className="text-2xl font-bold font-heading text-slate-900">{contracts.length}</p>
                <p className="text-sm text-slate-500">Total Contracts</p>
              </div>
            </CardContent>
          </Card>
          <Card className="sag-card">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="p-3 rounded-xl bg-emerald-100 text-emerald-600"><CheckCircle2 className="h-6 w-6" /></div>
              <div>
                <p className="text-2xl font-bold font-heading text-slate-900">{contracts.filter(c => c.is_active).length}</p>
                <p className="text-sm text-slate-500">Active Contracts</p>
              </div>
            </CardContent>
          </Card>
          <Card className="sag-card">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="p-3 rounded-xl bg-amber-100 text-amber-600"><Clock className="h-6 w-6" /></div>
              <div>
                <p className="text-2xl font-bold font-heading text-slate-900">{contracts.reduce((sum, c) => sum + (c.total_hours || 0), 0)}</p>
                <p className="text-sm text-slate-500">Total Hours Allocated</p>
              </div>
            </CardContent>
          </Card>
          <Card className="sag-card cursor-pointer hover:ring-2 hover:ring-amber-300 transition-all" onClick={() => setShowAlerts(!showAlerts)} data-testid="sla-alerts-card">
            <CardContent className="p-6 flex items-center gap-4">
              <div className={`p-3 rounded-xl ${alerts.length > 0 ? "bg-red-100 text-red-600" : "bg-slate-100 text-slate-400"}`}>
                {alerts.length > 0 ? <BellRing className="h-6 w-6" /> : <Bell className="h-6 w-6" />}
              </div>
              <div>
                <p className="text-2xl font-bold font-heading text-slate-900">{alerts.length}</p>
                <p className="text-sm text-slate-500">SLA Alerts</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* SLA Alerts Panel */}
        {showAlerts && (
          <Card className="sag-card border-amber-200" data-testid="sla-alerts-panel">
            <CardHeader className="border-b border-slate-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <BellRing className="h-5 w-5 text-amber-600" />
                  <CardTitle className="font-heading text-lg">SLA Usage Alerts</CardTitle>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setShowAlerts(false)}>Close</Button>
              </div>
              <CardDescription>Email alerts sent when clients reach 80% or 100% of their allocated hours</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              {alerts.length === 0 ? (
                <div className="p-8 text-center">
                  <Bell className="h-10 w-10 mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500">No alerts have been triggered yet</p>
                  <p className="text-sm text-slate-400 mt-1">Alerts are sent automatically at 80% and 100% SLA usage</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {alerts.map((alert) => (
                    <div key={alert.id} className="p-4 flex items-center justify-between hover:bg-slate-50" data-testid={`sla-alert-${alert.id}`}>
                      <div className="flex items-center gap-3">
                        <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${alert.alert_type === "exhausted" ? "bg-red-500" : "bg-amber-500"}`} />
                        <div>
                          <p className="font-medium text-slate-900">
                            {alert.contract_title}
                            <Badge className={`ml-2 ${alert.alert_type === "exhausted" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`} variant="secondary">
                              {alert.threshold}%{alert.alert_type === "exhausted" ? " — Exhausted" : " Warning"}
                            </Badge>
                          </p>
                          <p className="text-sm text-slate-500">
                            {alert.client_name} — {alert.hours_used}h / {alert.total_hours}h used ({alert.percent_consumed}%)
                          </p>
                          <p className="text-xs text-slate-400 mt-1">
                            Sent {new Date(alert.created_at).toLocaleDateString()} to {alert.emails_sent_to?.length || 0} recipient(s)
                          </p>
                        </div>
                      </div>
                      <Button variant="ghost" size="icon" onClick={() => handleDeleteAlert(alert.id)} title="Dismiss alert" data-testid={`dismiss-alert-${alert.id}`}>
                        <Trash2 className="h-4 w-4 text-slate-400" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Contracts Table */}
        <Card className="sag-card">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="font-heading text-lg">All Contracts</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-slate-500">Loading contracts...</div>
            ) : contracts.length === 0 ? (
              <div className="p-8 text-center">
                <FileText className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">No SLA contracts yet. Create one to get started!</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Contract</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Cycle</TableHead>
                    <TableHead>Hours Usage</TableHead>
                    <TableHead>Overage</TableHead>
                    <TableHead>Payment</TableHead>
                    <TableHead>Alerts</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {contracts.map((contract) => {
                    const summary = summaries[contract.id];
                    const pct = summary ? summary.percent_consumed : 0;
                    const progressColor = pct >= 90 ? "bg-red-500" : pct >= 70 ? "bg-amber-500" : "bg-emerald-500";
                    return (
                      <TableRow key={contract.id} data-testid={`sla-row-${contract.id}`}>
                        <TableCell>
                          <div className="font-medium text-slate-900">{contract.title}</div>
                          {contract.product_name && <div className="text-xs text-slate-500">{contract.product_name}</div>}
                        </TableCell>
                        <TableCell>
                          <div className="text-slate-700">{contract.client_name}</div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">{cycleLabels[contract.billing_cycle]}</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="w-40">
                            <div className="flex justify-between text-xs text-slate-500 mb-1">
                              <span>{summary ? summary.hours_used : 0}h used</span>
                              <span>{contract.total_hours}h total</span>
                            </div>
                            <div className="w-full bg-slate-100 rounded-full h-2">
                              <div className={`h-2 rounded-full transition-all ${progressColor}`} style={{ width: `${Math.min(pct, 100)}%` }} />
                            </div>
                            <div className="text-xs text-slate-400 mt-1">{summary ? summary.hours_remaining : contract.total_hours}h remaining</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={overageColors[contract.overage_handling]} variant="secondary">
                            {overageLabels[contract.overage_handling]}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="text-xs">
                            <p className="capitalize font-medium">{(contract.payment_method_type || "card").replace("_", " ")}</p>
                            <p className="text-slate-500 capitalize">{(contract.payment_processor || "fiserv") === "fiserv" ? "Scotiabank" : (contract.payment_processor || "fiserv").replace("_", " ")}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          {alertsByContract[contract.id]?.length > 0 ? (
                            <div className="flex items-center gap-1.5">
                              {alertsByContract[contract.id].some(a => a.alert_type === "exhausted") ? (
                                <Badge className="bg-red-100 text-red-700 gap-1" variant="secondary" data-testid={`alert-badge-${contract.id}`}>
                                  <BellRing className="h-3 w-3" /> 100%
                                </Badge>
                              ) : (
                                <Badge className="bg-amber-100 text-amber-700 gap-1" variant="secondary" data-testid={`alert-badge-${contract.id}`}>
                                  <AlertTriangle className="h-3 w-3" /> 80%
                                </Badge>
                              )}
                              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleResetAlerts(contract.id)} title="Reset alerts for this contract" data-testid={`reset-alerts-${contract.id}`}>
                                <RotateCcw className="h-3 w-3 text-slate-400" />
                              </Button>
                            </div>
                          ) : (
                            <span className="text-xs text-slate-400">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {contract.is_active ? (
                              <Badge className="bg-emerald-100 text-emerald-700" variant="secondary">Active</Badge>
                            ) : (
                              <Badge className="bg-slate-100 text-slate-700" variant="secondary">Inactive</Badge>
                            )}
                            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => openEdit(contract)} data-testid={`edit-sla-${contract.id}`}>
                              Edit
                            </Button>
                            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => handleDelete(contract)} data-testid={`delete-sla-${contract.id}`}>
                              Delete
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Edit Contract Dialog */}
      <Dialog open={editOpen} onOpenChange={(open) => { if (!open) { setEditOpen(false); setEditContract(null); } }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Edit SLA Contract</DialogTitle>
            <DialogDescription>Update contract terms, billing, and response times.</DialogDescription>
          </DialogHeader>
          {editContract && (
            <form onSubmit={handleEdit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Contract Title</Label>
                <Input value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} data-testid="edit-sla-title" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Total Hours</Label>
                  <Input type="number" step="0.5" min="1" value={editForm.total_hours} onChange={(e) => setEditForm({ ...editForm, total_hours: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Monthly Fee ($)</Label>
                  <Input type="number" step="0.01" min="0" value={editForm.monthly_fee} onChange={(e) => setEditForm({ ...editForm, monthly_fee: e.target.value })} data-testid="edit-sla-monthly-fee" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Hourly Rate ($)</Label>
                  <Input type="number" step="0.01" min="0" value={editForm.hourly_rate} onChange={(e) => setEditForm({ ...editForm, hourly_rate: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Overage Rate ($)</Label>
                  <Input type="number" step="0.01" min="0" value={editForm.overage_rate} onChange={(e) => setEditForm({ ...editForm, overage_rate: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Billing Cycle</Label>
                  <Select value={editForm.billing_cycle} onValueChange={(v) => setEditForm({ ...editForm, billing_cycle: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                      <SelectItem value="annual">Annual</SelectItem>
                      <SelectItem value="custom">Custom</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Overage Handling</Label>
                  <Select value={editForm.overage_handling} onValueChange={(v) => setEditForm({ ...editForm, overage_handling: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="alert_only">Alert Only</SelectItem>
                      <SelectItem value="allow_bill">Allow + Bill Extra</SelectItem>
                      <SelectItem value="block">Block Work</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Response Time (Standard)</Label>
                  <Input placeholder="e.g., 24 hours" value={editForm.response_time_standard} onChange={(e) => setEditForm({ ...editForm, response_time_standard: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Response Time (Emergency)</Label>
                  <Input placeholder="e.g., 4 hours" value={editForm.response_time_emergency} onChange={(e) => setEditForm({ ...editForm, response_time_emergency: e.target.value })} />
                </div>
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input type="date" value={editForm.contract_end} onChange={(e) => setEditForm({ ...editForm, contract_end: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Payment Method</Label>
                  <Select value={editForm.payment_method_type} onValueChange={(v) => setEditForm({ ...editForm, payment_method_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="card">Credit Card</SelectItem>
                      <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                      <SelectItem value="hybrid">Hybrid</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Processor</Label>
                  <Select value={editForm.payment_processor} onValueChange={(v) => setEditForm({ ...editForm, payment_processor: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fiserv">Scotiabank</SelectItem>
                      <SelectItem value="stripe">Stripe</SelectItem>
                      <SelectItem value="manual">Manual / Bank Transfer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* ==== Auto-Charge / Card on File Panel ==== */}
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4" data-testid="auto-charge-panel">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="h-4 w-4 text-sag-navy" />
                    <span className="font-semibold text-sm text-slate-900">Auto-Charge & Payment Method</span>
                  </div>
                  {autoChargeStatus?.card && !autoChargeStatus.card.expired && (
                    <Badge className="bg-emerald-100 text-emerald-700" variant="secondary" data-testid="auto-charge-active-badge">Active</Badge>
                  )}
                  {autoChargeStatus?.card?.expired && (
                    <Badge className="bg-red-100 text-red-700" variant="secondary" data-testid="auto-charge-expired-badge">Card Expired</Badge>
                  )}
                </div>

                <div className="space-y-3">
                  <div>
                    <Label className="text-xs text-slate-600 mb-1.5 block">Payment Source</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => setEditForm({ ...editForm, payment_method_source: "card_on_file", auto_charge: true })}
                        className={`text-left rounded-md border px-3 py-2 transition ${editForm.payment_method_source === "card_on_file" ? "border-sag-navy bg-white shadow-sm" : "border-slate-200 bg-white hover:border-slate-300"}`}
                        data-testid="pm-source-card"
                      >
                        <div className="flex items-center gap-2">
                          <CreditCard className={`h-4 w-4 ${editForm.payment_method_source === "card_on_file" ? "text-sag-navy" : "text-slate-400"}`} />
                          <span className="text-sm font-medium">Card on File</span>
                        </div>
                        <p className="text-[11px] text-slate-500 mt-0.5">Auto-billed via Stripe</p>
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditForm({ ...editForm, payment_method_source: "bank_transfer", auto_charge: false })}
                        className={`text-left rounded-md border px-3 py-2 transition ${editForm.payment_method_source === "bank_transfer" ? "border-sag-navy bg-white shadow-sm" : "border-slate-200 bg-white hover:border-slate-300"}`}
                        data-testid="pm-source-bank"
                      >
                        <div className="flex items-center gap-2">
                          <FileText className={`h-4 w-4 ${editForm.payment_method_source === "bank_transfer" ? "text-sag-navy" : "text-slate-400"}`} />
                          <span className="text-sm font-medium">Bank Transfer</span>
                        </div>
                        <p className="text-[11px] text-slate-500 mt-0.5">Manual reconciliation</p>
                      </button>
                    </div>
                  </div>

                  {editForm.payment_method_source === "card_on_file" && (
                    <div className="rounded-md border border-slate-200 bg-white p-3 space-y-2" data-testid="card-on-file-details">
                      {autoChargeStatus?.card ? (
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <CreditCard className="h-4 w-4 text-emerald-600" />
                            <span className="text-sm font-medium text-slate-800">
                              {autoChargeStatus.card.brand?.toUpperCase()} •••• {autoChargeStatus.card.last4}
                            </span>
                            <span className="text-xs text-slate-500">
                              exp {String(autoChargeStatus.card.exp_month).padStart(2, "0")}/{autoChargeStatus.card.exp_year}
                            </span>
                          </div>
                          <Button type="button" size="sm" variant="outline" onClick={handleSendCardSetupLink} disabled={sendingCardLink} data-testid="update-card-btn">
                            {sendingCardLink ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Send className="h-3 w-3 mr-1" />}
                            Update
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                          <div className="text-sm text-slate-600 flex-1 min-w-[200px]">
                            <p className="font-medium">No card on file yet</p>
                            <p className="text-xs text-slate-500">Send the client a secure Stripe link — they save their card once, we never see it. Or reuse an existing card already on their Stripe customer.</p>
                          </div>
                          <div className="flex flex-col gap-2">
                            <Button type="button" size="sm" onClick={handleSendCardSetupLink} disabled={sendingCardLink} className="bg-sag-navy hover:bg-sag-navy-800" data-testid="send-card-setup-link-btn">
                              {sendingCardLink ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Send className="h-3 w-3 mr-1" />}
                              Send Secure Card Setup Link
                            </Button>
                            <Button type="button" size="sm" variant="outline" onClick={() => handleAttachExistingCard()} disabled={sendingCardLink} data-testid="attach-existing-card-btn">
                              <CreditCard className="h-3 w-3 mr-1" />
                              Use Existing Stripe Card
                            </Button>
                            <Button type="button" size="sm" variant="ghost" onClick={handleAttachByPI} disabled={sendingCardLink} data-testid="attach-by-pi-btn" className="text-slate-600 hover:text-sag-navy">
                              <FileText className="h-3 w-3 mr-1" />
                              Reconcile from PaymentIntent ID
                            </Button>
                          </div>
                        </div>
                      )}

                      <div className="flex items-center gap-2 pt-1 border-t border-slate-100">
                        <input
                          type="checkbox"
                          id="edit-auto-charge"
                          checked={editForm.auto_charge}
                          onChange={(e) => setEditForm({ ...editForm, auto_charge: e.target.checked })}
                          className="rounded border-slate-300"
                          data-testid="auto-charge-toggle"
                        />
                        <Label htmlFor="edit-auto-charge" className="cursor-pointer text-sm">
                          Auto-charge on next billing date
                        </Label>
                      </div>

                      {editForm.auto_charge && autoChargeStatus?.next_billing_date && (
                        <div className="bg-emerald-50 border border-emerald-100 rounded-md p-2 text-xs">
                          <p className="text-emerald-800">
                            <strong>Next charge:</strong> ${(autoChargeStatus.monthly_fee || 0).toFixed(2)} on {autoChargeStatus.next_billing_date} ({autoChargeStatus.billing_cycle})
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {editForm.payment_method_source === "bank_transfer" && (
                    <div className="rounded-md border border-blue-100 bg-blue-50 p-3 text-xs text-blue-800" data-testid="bank-transfer-note">
                      <strong>Bank Transfer Mode:</strong> This SLA will never be auto-charged. The scheduler generates invoices and emails them; the client remits payment manually. Reconcile via the Admin → Invoices workflow.
                    </div>
                  )}
                </div>
              </div>
              {/* ==== end Auto-Charge Panel ==== */}
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} rows={2} />
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="edit-active" checked={editForm.is_active} onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })} className="rounded border-slate-300" />
                <Label htmlFor="edit-active" className="cursor-pointer">Active</Label>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
                <Button type="submit" className="bg-sag-navy hover:bg-sag-navy-800" data-testid="save-edit-sla">Save Changes</Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
